import { Property } from "js/components/types";
import { PROPERTY_USE_OPTIONS } from "js/constants/property-use";
import { PropertyLocal } from "js/pages/Tenants/types";

const normalizeUsageKey = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const PROPERTY_USE_MAP = PROPERTY_USE_OPTIONS.reduce<Record<string, string>>(
  (acc, option) => {
    const key = normalizeUsageKey(option.value);
    acc[key] = option.value;
    return acc;
  },
  {},
);

/**
 * Normalizes property usage values from the backend to match the allowed options.
 * Handles variations in casing and formatting to ensure consistent selection behavior.
 */
const normalizePropertyUsage = (usage: string | null): string => {
  if (!usage) return "";

  // Normalize the input by trimming, removing accents, and converting to lowercase
  const normalized = normalizeUsageKey(usage);

  // Return the mapped value or the original if no match found
  return PROPERTY_USE_MAP[normalized] || usage;
};

export const transformPropertyToLocal = (property: Property): PropertyLocal => {
  const address = property.address;
  const streetAddress = address
    ? `${address.street || ""} ${address.exterior_number || ""} ${
        address.interior_number || ""
      }`.trim()
    : "";

  const label = property.alias || streetAddress;

  return {
    id: property.id || `prop-${Date.now()}`,
    label: label,
    alias: property.alias || "",
    direccion: streetAddress,
    colonia: address?.neighborhood || "",
    uso: normalizePropertyUsage(property.usage),
    rental_price: String(property.rental_price || ""),
    maintenance_price: String(property.maintenance_price || ""),
    street: address?.street || "",
    exterior_number: address?.exterior_number || "",
    interior_number: address?.interior_number || "",
    zip_code: address?.zip_code || "",
    city: address?.city || "",
    state: address?.state || "",
  };
};

export const transformPropertiesToLocal = (
  properties: Property[],
): PropertyLocal[] => {
  return properties.map(transformPropertyToLocal);
};
